import { faCircle } from "@fortawesome/free-regular-svg-icons";
import { faInfo } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ConfirmationModal from "Components/Atoms/ConfirmationModal";
import Tooltip from "Components/Atoms/Tooltip";
import { leanEntries } from "Constants/defaultStyles";
import { CLEAR_ANSWERS_CONFIRMATION_TEXT } from "Constants/texts";
import { selectDataWasFetched, selectUserData } from "Redux/UserSlice";
import { useAppDispatch, useAppSelector } from "Redux/hooks";
import { selectStyle } from "Redux/appSlice";
import {
  activateManualForQuestion,
  emptyModule,
  iterateModule,
  removeTerm,
  setActiveIndex,
  setCurrentChapter,
} from "feature/app/actions";
import useBeforeUnload from "hooks/useBeforeUnload";
import useConfirmation from "hooks/useConfirmation";
import { useEffect, useRef, useState } from "react";
import ReactDOMServer from "react-dom/server";
import { Provider, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Button from "../Components/Atoms/Button";
import ModuleShower from "../Components/Atoms/ModuleShower";
import NavigatorChevron from "../Components/Atoms/NavigatorChevron";
import RichTextReader from "../Components/Atoms/RichTextReader";
import Switch from "../Components/Atoms/Switch";
import TableOfContent from "../Components/Atoms/TableOfContent";
import HoverPage from "../Components/Molecule/HoverPage";
import ModuleNavigator from "../Components/Molecule/ModuleNavigator";
import PopUpAddElements from "../Components/Molecule/PopUpAddElements";
import QuestionModule, { MANUAL_TERM_ID } from "../Components/Molecule/QuestionModule";
import { postSendEmail } from "../Logic/ModuleApi";
import interpretStep from "../Logic/interpretStep";
import Layout from "../Pages/Layout";
import {
  fetchUserProgresses,
  loadModuleByIds,
  selectActiveIndex,
  selectAreRefsVisible,
  selectError,
  selectIsHalfPage,
  selectLoading,
  selectModule,
  selectModuleState,
  selectSelectedSteps,
  selectTableOfContent,
  selectTerms,
  selectToBePrinted,
  selectToResetCallback,
  selectUserProgresses,
  setLoading,
  setRefs,
  setReset,
  setTableOfContent,
  setUserProgress,
} from "../Redux/appSlice";
import store from "../Redux/store";
import EmailModal from "./components/EmailModal";

let toMove = false;

export default function Module() {
  useBeforeUnload("Changes you made will not be saved.");
  const params = useParams();
  const { color2 } = useAppSelector(selectStyle);
  const { moduleId = "", orgId = "" } = params;
  const dispatch = useAppDispatch();
  const tableOfContent = useAppSelector(selectTableOfContent);
  const isHalfPage = useAppSelector(selectIsHalfPage);
  const error = useAppSelector(selectError);
  const loading = useAppSelector(selectLoading);
  const toResetCallback = useAppSelector(selectToResetCallback);
  const toBePrinted = useAppSelector(selectToBePrinted);
  const refs = useAppSelector(selectAreRefsVisible);
  const terms = useAppSelector(selectTerms);
  const module = useAppSelector(selectModule);
  const moduleState = useAppSelector(selectModuleState);
  const userProgresses = useAppSelector(selectUserProgresses);
  const userData = useAppSelector(selectUserData);
  const dataWasFetched = useAppSelector(selectDataWasFetched);
  const selectedSteps = useAppSelector(selectSelectedSteps);

  const navigate = useNavigate();

  const [toApprove, setToApprove] = useState<{
    id: any;
    questionState: any;
  } | null>(null);
  const activeIndex = useSelector(selectActiveIndex);
  const [toReset, setToReset] = useState(false);
  const [canGoDown, setCanGoDown] = useState(true);

  const [toSend, setToSend] = useState<{
    type: "Email" | "Progress" | "print";
    toBePrinted?: string;
  } | null>(null);

  useEffect(() => {
    if (userData?.user_id !== undefined && userProgresses === undefined) {
      dispatch(
        fetchUserProgresses({
          moduleId: moduleId,
          userId: userData?.user_id || "",
        })
      );
    }
  }, [dispatch, moduleId, userData, userData?.user_id, userProgresses]);

  useEffect(() => {
    if (module?.module_id !== params.moduleId) {
      dispatch(emptyModule());
      dispatch(removeTerm("all"));
    }

    const loadModule = () => {
      dispatch(setLoading());
      setTimeout(
        () =>
          dispatch(
            loadModuleByIds({
              organizationId: orgId,
              moduleId: moduleId,
            })
          ),
        500
      );
    };
    if (window.userToken && dataWasFetched)
      loadModule();
    window.onbeforeunload = () => {
      loadModule();
    };
  }, [window.userToken, dataWasFetched]);

  useEffect(() => {
    const elem = document.getElementById(String(activeIndex));

    if (elem) animateNavigation(elem);
    if (module)
      if (moduleState) {
        const step = selectedSteps[activeIndex].step;
        const question =
          step && document.getElementById(`question-body-${step}`);
        if (question) {
          question.scrollTo({ top: 0, behavior: "smooth" });
        }
        if (
          selectedSteps[activeIndex].step !== "toPrint" &&
          step !== undefined
        ) {
          dispatch(
            setCurrentChapter(moduleState.map.questions[step].chapterPos)
          );
        }else{
          dispatch(
            setCurrentChapter(-1)
          );
        }
      }
  }, [activeIndex]);

  useEffect(() => {
    if (toMove) {
      navigateQuestions("down");
      toMove = false;
    }
  }, [moduleState]);

  const termListId = "term-list";
  const termListRef = useRef<{ id: string; title?: string }[]>();

  useEffect(() => {
    const termListDiv = document.getElementById(termListId);

    if (terms && termListRef.current && termListRef.current.length) {
      const lastInRef = termListRef.current.slice(-1).pop();
      const lastInState = terms.slice(-1).pop();

      const isTermNewAdded = lastInRef?.title !== lastInState?.title;

      if (termListDiv && isTermNewAdded) {
        termListDiv.scrollIntoView({ behavior: "smooth", block: "end" });
      }
    }
    termListRef.current = terms;
  }, [terms]);

  useEffect(() => {
    const toPrint = document.getElementById("toPrint");

    if (toPrint !== null) {
      toPrint.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, [toBePrinted]);

  const animateNavigation = (element: HTMLElement) => {
    const targetElement = document.getElementById("steps");
    if (targetElement) {
      targetElement.style.transform = `translateY(-${element.offsetTop}px)`;
      targetElement.style.transition = "1s ease-in-out";
    }
  };

  const navigateQuestions = (
    direction: "up" | "down",
    conclusion?: boolean
  ) => {
    if (!moduleState) return;

    if (direction === "up") {
      if (activeIndex > 0) {
        dispatch(setActiveIndex(activeIndex - 1));
      }
    } else if (direction === "down") {
      if (
        activeIndex + 1 < selectedSteps.length &&
        (selectedSteps[activeIndex].iterated || conclusion)
      ) {
        dispatch(setActiveIndex(activeIndex + 1));
      }
    }
  };

  const shouldShowButton = module?.export_types?.some((item) => item.name === "print");

   // Function to handle printing of specific HTML
   const handlePrint = (htmlContent: string) => {
    // Create a new window
    const printWindow = window.open("", "", "width=800,height=600");
    
    // Inject HTML content into the new window
    if (printWindow) {
      printWindow.document.write("This is the Final Report from " + module?.module_name)
      printWindow.document.write(htmlContent);
      printWindow.document.title = module?.module_name + " " + userData?.email_address + " " + new Date();
      printWindow.document.write("<br /> <br />Where needed, please contact the Entries application provider Lean Entries directly at info@leanentries.com");
      printWindow.document.write(`
        <style>
          body {
            font-family: Arial, sans-serif;
            color: black;
          }
          h1 {
            color: blue;
          }
          p {
            font-size: 14px;
          }
        </style>
      `);

      // Close the document to render the content
      printWindow.document.close();

      // Trigger the print dialog
      printWindow.print();

      // Optional: Close the print window after printing
      printWindow.close();
    }
    else
    {
      alert("Unable to open print window. Please check your popup blocker settings.");
    }
  };

  const onAnswerSelection = (id: any, questionState: any) => {
    if (!module || !moduleState) return;
    const selectedTriggers = Object.keys(questionState).filter(
      (key) => questionState[key]
    );
    const stepId =
      module.chapters[id.chapterPos].questions[id.questionPos].question_id;
    const selectedStep = selectedSteps.filter(
      (step) => step.step === stepId
    )[0];

    const previousAnswers = [...(selectedStep.answers || [])];
    if (
      selectedStep.iterated &&
      previousAnswers.sort().join(",") === selectedTriggers.sort().join(",")
    ) {
      navigateQuestions("down");
    } else if (selectedStep.iterated && toApprove === null) {
      setToApprove({ id: id, questionState: questionState });
    } else {
      dispatch(
        iterateModule({
          selectedStep: id,
          selectedTriggers: selectedTriggers,
        })
      );
      toMove = true;
    }
  };

  const [
    isConfirmNavigationToHome,
    confirmNavigateHome,
    onConfirmNavigateToHome,
    onCancelNavigateToHome,
  ] = useConfirmation({
    onConfirm: () => {
      navigate("/");
    },
  });

  if (loading) {
    return (
      <Layout>
        <div className="m-auto mt-24 flex h-screen w-screen flex-col justify-center">
          <FontAwesomeIcon icon={faCircle} bounce size="4x" />
        </div>
      </Layout>
    );
  }

  if (!moduleState) return null;

  if (!loading && error) {
    return (
      <Layout>
        <div className="m-auto mt-24 flex w-4/5 flex-col ">
          <h1 className="text-3xl">Something went wrong</h1>
          <p>please try again</p>
        </div>
      </Layout>
    );
  }

  return (
    <div className="relative">
      <ConfirmationModal
        isOpen={toApprove !== null}
        content="By changing the answer the following answers will be changed and may re-emerge later. Do you want to continue?"
        onContinue={() => {
          onAnswerSelection(toApprove?.id, toApprove?.questionState);
          setToApprove(null);
        }}
        onCancel={() => {
          setToReset(true);
          setToApprove(null);
        }}
      />

      <ConfirmationModal
        isOpen={!!toResetCallback}
        content={CLEAR_ANSWERS_CONFIRMATION_TEXT}
        onContinue={() => {
          toResetCallback();
        }}
        onCancel={() => {
          dispatch(setReset(null));
        }}
      />

      <ConfirmationModal
        isOpen={isConfirmNavigationToHome}
        content={CLEAR_ANSWERS_CONFIRMATION_TEXT}
        onContinue={onConfirmNavigateToHome}
        onCancel={onCancelNavigateToHome}
      />

      <EmailModal
        isOpen={!!toSend && toSend.type === "Email"}
        defaultValue={userData?.email_address}
        onContinue={async (email) => {
          if (module !== null) {
            await postSendEmail({
              email: email,
              content: toSend?.toBePrinted || "",
              custom: {
                module_name: module.module_name,
                module_id: module.module_id,
              },
            })
              .then(() => setToSend(null))
              .catch(() => setToSend(null));
          }
        }}
        onCancel={() => setToSend(null)}
      />

      <ConfirmationModal
        isOpen={!!toSend && toSend.type === "print"}
        content= "Are you sure that you want print the Final Report?"
        onContinue={() => {
          handlePrint(toSend?.toBePrinted || "");
          setToSend(null);
        }}        
        onCancel={() => {
          setToSend(null);
        }}
      />

      {toSend && toSend.type === "Progress" && (
        <PopUpAddElements
          type={toSend.type}
          onSet={async (type, val) => {
            if (!userData) {
              return;
            }

            if (type === "Progress" && userData.user_id !== undefined) {
              if (module && moduleState) {
                dispatch(
                  setUserProgress({
                    userId: userData.user_id,
                    moduleId: module.module_id,
                    name: val.name,
                    data: moduleState.initialization,
                    toBePrinted,
                  })
                );
                setToSend(null);
              }
            }
          }}
          defaultValue={{ Email: userData?.email_address }}
          onCancel={() => setToSend(null)}
        />
      )}

      <Layout
        barAddOns={
          <div className="flex w-full">
            <div className="m-auto hidden text-center md:block">
              <ModuleShower />
            </div>
            <div className="lg:align-center col-start-3 mx-4 my-auto flex flex-row-reverse items-center">
              <Tooltip text="Table of Contents view to navigate">
                <Switch
                  label="ToC"
                  color={color2}
                  onClick={() => dispatch(setTableOfContent())}
                  checked={tableOfContent}
                />
              </Tooltip>
              <Tooltip text="Show references">
                <Switch
                  label="Refs"
                  color={color2}
                  onClick={() => dispatch(setRefs())}
                  checked={refs}
                />
              </Tooltip>
              <Tooltip text="User Manual">
                <div
                  className="ml-2 mr-2 flex h-5 w-5 items-center justify-center rounded-full bg-gray-300 text-black transition-colors hover:bg-gray-200 md:h-9 md:w-9"
                  onClick={() => {
                    const currentQuestionId = selectedSteps[activeIndex].step;
                    if (currentQuestionId) {
                      Promise.resolve(dispatch(activateManualForQuestion(currentQuestionId))).then(() => {
                        const termListDiv = document.getElementById(termListId + currentQuestionId);
                        const termElement = document.getElementById(module?.user_manual_term_id ? module?.user_manual_term_id : MANUAL_TERM_ID);
                        if (termListDiv && termElement) {
                          termListDiv.scrollTo({
                            top: termElement.offsetTop - termListDiv.offsetTop,
                            behavior: "smooth",
                          });
                        }
                      }) ;
                    }
                  }}
                >
                  <FontAwesomeIcon icon={faInfo} />
                </div>
              </Tooltip>
            </div>
          </div>
        }
      >
        {moduleState && (
          <>
            <ModuleNavigator
              onClick={(i) => {
                if (i !== undefined) dispatch(setActiveIndex(i));
              }}
            />
          </>
        )}
        {tableOfContent && module && moduleState && (
          <div className="overflow-hidden">
            <HoverPage
              onClose={() => dispatch(setTableOfContent())}
              direction="right"
              halfPage={isHalfPage}
            >
              <TableOfContent
                selectedSteps={
                  selectedSteps.filter((step, i) => {
                    if (i === 0) return true;
                    return moduleState?.initialization.selectedSteps[i - 1]
                      .iterated;
                  }) as any
                }
                moduleMap={moduleState.map}
                module={module}
                currentStep={activeIndex}
                onQuestionClick={(i) => {
                  if (!isHalfPage) dispatch(setTableOfContent());
                  dispatch(setActiveIndex(i));
                }}
              />
            </HoverPage>
          </div>
        )}
        <div
          className="overflow-hidden"
          style={{ backgroundColor: leanEntries.color2 }}
        >
          <div id="steps">
            {selectedSteps.map((step, i) => {
              if (step.step === "toPrint") {
                let toSendData: any[] = [];
                if (toBePrinted) {
                  toSendData = toBePrinted.map((p, p_i) => {
                    return (
                      <div key={p_i}>
                        <RichTextReader
                          text={JSON.parse(p.content).text}
                          justText={false}
                        />
                      </div>
                    );
                  });
                }
                return (
                  <div key={i} id={i.toString()} className="relative">
                    {activeIndex !== 0 && (
                      <NavigatorChevron
                        onClick={() => navigateQuestions("up")}
                        direction="up"
                      />
                    )}
                    <div
                      className={`relative h-screen w-11/12 max-w-[450px] overflow-y-auto pt-32 transition sm:w-4/5 md:w-[700px] md:max-w-none lg:w-1/2 lg:pl-5 lg:pr-10 lg:pt-36 ${
                        isHalfPage && tableOfContent ? "ml-14" : "m-auto"
                      }`}
                    >
                      <div className="h-[99%] overflow-y-auto" id="toPrint">
                        <div>
                          <span style={{ color: color2 }} className="text-[1.6em] font-semibold">
                            FINAL REPORT
                          </span>
                          {toBePrinted && toSendData}
                        </div>
                        <div className="flex w-full flex-col">
                          <Button
                            label="SEND AS EMAIL"
                            className="mb-2 w-full"
                            onClick={() =>
                              setToSend({
                                type: "Email",
                                toBePrinted: ReactDOMServer.renderToString(
                                  <Provider store={store}>
                                    {toSendData}
                                  </Provider>
                                ),
                              })
                            }
                          />
                         {shouldShowButton && (<Button
                            label="PRINT"
                            className="mb-2 w-full"
                            onClick={() =>
                              setToSend({
                                type: "print",
                                toBePrinted: ReactDOMServer.renderToString(
                                  <Provider store={store}>
                                    {toSendData}
                                  </Provider>
                                ),
                              })
                            }
                          />
                           )}
                          <Button
                            label="SAVE AS PROGRESS"
                            className="mb-2 w-full"
                            onClick={() =>
                              setToSend({
                                type: "Progress",
                              })
                            }
                          />
                          <Button
                            onClick={confirmNavigateHome}
                            label="BACK TO MODULES MAIN PAGE"
                            className="mb-2 w-full"
                          />
                        </div>
                      </div>
                    </div>
                    {moduleState &&
                      i !== selectedSteps.length - 1 &&
                      selectedSteps[i].iterated && (
                        <NavigatorChevron
                          onClick={() =>
                            navigateQuestions("down", question.conclusion)
                          }
                          direction="down"
                        />
                      )}
                  </div>
                );
              }
              if (!step.step || !module) return null;

              const question = interpretStep(
                moduleState?.map.questions[step.step],
                module
              );
              return (
                <div key={i} id={i.toString()} className="relative">
                  {activeIndex !== 0 && (
                    <NavigatorChevron
                      onClick={() => navigateQuestions("up")}
                      direction="up"
                    />
                  )}
                  <QuestionModule
                    selected={step.answers || []}
                    type={question.multiple}
                    title={question.title}
                    answers={question.answers}
                    onChange={(questionState) =>
                      onAnswerSelection(question.id, questionState)
                    }
                    links={
                      refs ? [{ label: "test", href: "test", id: "1" }] : []
                    }
                    toReset={{ value: toReset, toChange: setToReset }}
                    conclusion={question.conclusion}
                    setCanGoDown={setCanGoDown}
                    print={question.print}
                    id={step.step}
                    iterated={selectedSteps[i].iterated}
                    userManualTermId={module.user_manual_term_id}
                  />
                  {moduleState &&
                    i !== selectedSteps.length - 1 &&
                    selectedSteps[i].iterated &&
                    canGoDown && (
                      <NavigatorChevron
                        onClick={() =>
                          navigateQuestions("down", question.conclusion)
                        }
                        direction="down"
                        extraClassName="bg-white w-[100vw] -mb-2"
                      />
                    )}
                </div>
              );
            })}
          </div>
        </div>
      </Layout>
    </div>
  );
}
